// Links references in text the way GitHub does: the repo's autolink references
// (e.g. TUTORY-1270 → Jira) plus #123 → issue. GitHub's own bodyHTML already contains
// these links; this covers markdown rendered in the browser and plain-text titles.
import m from './m.js'
import { icon, iconSvg } from './icons.js'
import { filterQuery } from './lanes.js'
import { board } from './store.js'

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
const compiled = new WeakMap()

/** Rules for the open board: [{ prefix, url (with <num>), alphanumeric }]. */
function rules() {
  const data = board.data
  if (!data) return null
  if (compiled.has(data)) return compiled.get(data)
  const links = [
    ...(data.autolinks || []),
    { prefix: '#', url: `${data.repo.url}/issues/<num>`, alphanumeric: false },
  ]
  const byPrefix = new Map(links.map(l => [l.prefix.toLowerCase(), l]))
  // longest prefix first, so "TUTORY-" wins over "T"
  const prefixes = [...byPrefix.keys()]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
  const rule = {
    regex: new RegExp(
      `(?<![\\w/&-])(${prefixes.join('|')})([A-Za-z0-9]+)\\b`,
      'gi'
    ),
    byPrefix,
  }
  compiled.set(data, rule)
  return rule
}

/** Splits text into strings and { text, href } references. */
export function references(text) {
  const rule = rules()
  if (!rule || !text) return [text]
  const parts = []
  let last = 0
  for (const match of text.matchAll(rule.regex)) {
    const [whole, prefix, ref] = match
    const link = rule.byPrefix.get(prefix.toLowerCase())
    if (!link.alphanumeric && !/^\d+$/.test(ref)) continue
    parts.push(text.slice(last, match.index), {
      text: whole,
      href: link.url.replaceAll('<num>', ref),
      number: prefix === '#' ? Number(ref) : null,
    })
    last = match.index + whole.length
  }
  parts.push(text.slice(last))
  return parts.filter(p => p !== '')
}

/** Issue or PR number of a link into the open repo on GitHub, else null. */
export function issueNumber(href) {
  const url = board.data?.repo.url
  if (!url || !href?.startsWith(`${url}/`)) return null
  const match = href
    .slice(url.length)
    .match(/^\/(?:issues|pull)\/(\d+)(?:[/?#].*)?$/)
  return match ? Number(match[1]) : null
}

/** In-app URL of an issue of the open board, keeping the current filters; see followLink in layout.js. */
export function issueHref(number, tab = '') {
  const query = m.buildQueryString(filterQuery())
  return `/${board.key}/${number}${tab}${query ? `?${query}` : ''}`
}

/** A pull request's Files changed tab, see detailView. */
export function changesHref(number) {
  return issueHref(number, '/changes')
}

/** Same-repo reference: opens the modal, the GitHub icon after it goes to GitHub. */
export function issueRefView({ number, href, text }) {
  return [
    m('a.ref', { href: issueHref(number) }, text),
    m(
      'a.gh-ref',
      { href, target: '_blank', title: 'Open on GitHub' },
      icon('mark-github')
    ),
  ]
}

/** Text with references as links, for titles. */
export function linkify(text) {
  return references(text).map(part =>
    typeof part === 'string'
      ? part
      : part.number
        ? issueRefView(part)
        : m('a.ref', { href: part.href, target: '_blank' }, part.text)
  )
}

/**
 * Turns links to issues of the open repo in rendered HTML (GitHub's or ours) into
 * modal links with a GitHub icon after them. Safe to run repeatedly on the same DOM.
 */
export function issueLinks(dom) {
  for (const a of dom.querySelectorAll(
    'a[href]:not([data-number]):not(.gh-ref)'
  )) {
    const number = issueNumber(a.href)
    if (!number) continue
    const github = Object.assign(document.createElement('a'), {
      className: 'gh-ref',
      href: a.href,
      target: '_blank',
      title: 'Open on GitHub',
    })
    github.innerHTML = `<span class="octicon">${iconSvg('mark-github')}</span>`
    a.dataset.number = number
    a.href = issueHref(number)
    a.removeAttribute('target')
    a.after(github)
  }
}

/** Adds reference links to rendered HTML, skipping existing links and code. */
export function linkifyHtml(html) {
  if (!rules()) return html
  const template = document.createElement('template')
  template.innerHTML = html
  const walker = document.createTreeWalker(
    template.content,
    NodeFilter.SHOW_TEXT
  )
  const nodes = []
  while (walker.nextNode())
    if (!walker.currentNode.parentElement?.closest('a, code, pre'))
      nodes.push(walker.currentNode)
  for (const node of nodes) {
    const parts = references(node.data)
    if (parts.length === 1 && typeof parts[0] === 'string') continue
    node.replaceWith(
      ...parts.map(part =>
        typeof part === 'string'
          ? part
          : Object.assign(document.createElement('a'), {
              href: part.href,
              textContent: part.text,
            })
      )
    )
  }
  return template.innerHTML
}
